import apiClient from '@/shared/services/apiClient';

export type OrderType = 'custom_kit' | 'curated_enrollment' | 'recurring' | 'shop' | 'woocommerce' | 'other';

export interface UnifiedOrderSummary {
    id: string;
    order_number: string;
    created_at: string;
    type: OrderType;
    source: string;
    customer_name: string;
    customer_email: string;
    organization_id: number;
    organization_name: string | null;
    total_cents: number;
    patient_total_cents: number;
    employer_total_cents: number;
    payment_status: string | null;
    fulfillment_status: string;
    push_status: string | null;
    is_subscription: boolean;
}

export interface OrderPayment {
    transaction_id: string;
    provider_key: string;
    status: string;
    amount_cents: number;
    provider_payment_id: string | null;
    created_at: string;
}

export interface PaymentGroupOrder {
    id: string;
    order_number: string;
    type: OrderType;
    source: string;
    status: string;
    fulfillment_status: string;
    payment_allocated_cents: number;
    refund_amount_cents: number;
    cancellable: {ok: true} | {ok: false; reason: string};
    is_current: boolean;
}

export interface PaymentGroupMembership {
    id: number;
    plan: string;
    status: string;
    amount_cents: number;
    current_period_end: string;
    cancel_at_period_end: boolean;
}

export interface PaymentGroup {
    payment_id: string;
    amount_cents: number;
    refund_amount_cents: number;
    orders: PaymentGroupOrder[];
    membership: PaymentGroupMembership | null;
}

export interface RefundResult {
    refundedCents: number;
    totalRefundedCents: number;
    fullyRefunded: boolean;
}

export type MembershipCancelOutcome = 'deactivated' | 'renewal_stopped' | 'left_active' | 'none';

export interface SingleCancelResult {
    cancelled: true;
    refund: RefundResult | null;
    membership?: {id: number; outcome: MembershipCancelOutcome};
}

export interface PurchaseOrderOutcome {
    order_id: string;
    order_number: string;
    outcome: 'cancelled' | 'skipped' | 'failed';
    refund: RefundResult | null;
    reason?: string;
}

export interface PurchaseCancelResult {
    results: PurchaseOrderOutcome[];
    membership: {id: number | null; outcome: MembershipCancelOutcome};
}

export type CancelOrderResult = SingleCancelResult | PurchaseCancelResult;

export interface OrderLineItem {
    kind: 'product' | 'assay';
    name: string;
    quantity: number;
    unit_price_cents: number | null;
    total_cents: number | null;
    sku?: string | null;
}

export interface CycleOrder {
    order_id: string;
    order_number: string;
    created_at: string;
    total_cents: number;
    fulfillment_status: string;
}

export interface OrderSubscription {
    enrollment_id: number;
    status: string;
    enrollment_kind: string;
    next_test_due: string | null;
    cycle_history: CycleOrder[];
}

export interface UnifiedOrder extends UnifiedOrderSummary {
    customer_phone: string | null;
    user_id: number | null;
    provider_id: number | null;
    discount_cents: number;
    shipping_address: unknown;
    payment: OrderPayment | null;
    fulfillment: {
        status: string;
        tracking_number: string | null;
        tracking_carrier: string | null;
        shipped_at: string | null;
        push_status: string | null;
        shipstation_order_id: string | null;
        push_error: string | null;
        push_attempts: number;
    };
    subscription: OrderSubscription | null;
    line_items: OrderLineItem[];
    payment_group: PaymentGroup | null;
}

export interface OrderListResult {
    orders: UnifiedOrderSummary[];
    total: number;
    page: number;
    page_size: number;
}

export interface OrderListFilters {
    q?: string;
    type?: OrderType;
    payment_status?: string;
    fulfillment_status?: string;
    push_status?: string;
    organization_id?: number;
    date_from?: string;
    date_to?: string;
    page?: number;
    page_size?: number;
}

export interface SubscriptionSummary {
    enrollment_id: number;
    user_id: number;
    customer_name: string | null;
    organization_id: number | null;
    organization_name: string | null;
    program: string;
    enrollment_kind: string;
    status: string;
    next_test_due: string | null;
    patient_amount_cents: number;
    employer_amount_cents: number;
    enrolled_at: string;
}

export interface SubscriptionListResult {
    subscriptions: SubscriptionSummary[];
    total: number;
    page: number;
    page_size: number;
}

function toQuery(params: Record<string, unknown>): string {
    const usp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
    }
    const s = usp.toString();
    return s ? `?${s}` : '';
}

export const AdminOrdersService = {
    async list(filters: OrderListFilters): Promise<OrderListResult> {
        return apiClient.get<OrderListResult>(`/api/admin/orders${toQuery(filters as Record<string, unknown>)}`);
    },
    async detail(id: string): Promise<UnifiedOrder> {
        return apiClient.get<UnifiedOrder>(`/api/admin/orders/${id}`);
    },
    async subscriptions(page = 1, pageSize = 25, status = 'active'): Promise<SubscriptionListResult> {
        return apiClient.get<SubscriptionListResult>(
            `/api/admin/subscriptions${toQuery({page, page_size: pageSize, status})}`
        );
    },
    async cancelOrder(
        id: string,
        body: {reason?: string; refund?: boolean; scope?: 'order' | 'purchase'}
    ): Promise<CancelOrderResult> {
        return apiClient.post(`/api/admin/orders/${id}/cancel`, body);
    },
    async refundOrder(
        id: string,
        body: {amount_cents?: number}
    ): Promise<{refundedCents: number; totalRefundedCents: number; fullyRefunded: boolean}> {
        return apiClient.post(`/api/admin/orders/${id}/refund`, body);
    },
    async sendToShipStation(id: string): Promise<{result: string}> {
        return apiClient.post(`/api/admin/orders/${id}/shipstation`, {});
    },
    async subscriptionAction(
        id: number,
        action: 'cancel' | 'pause' | 'resume' | 'reschedule',
        body: {reason?: string; next_test_due?: string}
    ): Promise<{status: string}> {
        return apiClient.post(`/api/admin/subscriptions/${id}/${action}`, body);
    },
};

export default AdminOrdersService;
